import React from "react";
import { motion } from "framer-motion";

const PILLARS = [
  {
    kicker: "Systems",
    headline: "Make complex work simpler.",
    body:
      "I look for where work gets stuck, duplicated or unnecessarily manual — then design clearer processes and practical systems around the people doing the work.",
  },
  {
    kicker: "Impact",
    headline: "Build change that lasts.",
    body:
      "The goal isn't simply to launch something new. It's to create measurable improvements that continue working after implementation.",
  },
  {
    kicker: "People",
    headline: "Design around humans.",
    body:
      "Technology only works when people understand it, trust it and can actually use it. Good transformation starts with the people doing the work.",
  },
];

export default function Capabilities() {
  return (
    <section
      className="bg-[#FAF9F6] text-[#0B0B0C] relative overflow-hidden"
      data-testid="capabilities"
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #0B0B0C 1px, transparent 1.5px), radial-gradient(circle at 80% 70%, #0B0B0C 1px, transparent 1.5px)",
          backgroundSize: "36px 36px, 48px 48px",
        }}
        aria-hidden
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {/* Section header — compact but dramatic */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 lg:mb-16">
          <div className="lg:col-span-8">
            <p className="eyebrow !text-[#B98B24]">How I work</p>
            <h2
              className="mt-4 font-display leading-[0.94] tracking-[-0.028em] text-[#0B0B0C]"
              style={{ fontSize: "clamp(2.75rem, 6vw, 5.25rem)" }}
            >
              Systems.{" "}
              <span className="text-[#B98B24]">·</span>{" "}
              Impact.{" "}
              <span className="text-[#B98B24]">·</span>{" "}
              People.
            </h2>
            <p className="mt-5 text-[#343437] text-lg max-w-md">
              Different challenges. Same approach.
            </p>
          </div>
        </div>

        {/* Trajectory ribbon — compact */}
        <div className="hidden lg:block relative h-8 mb-10" aria-hidden>
          <svg viewBox="0 0 1200 40" className="w-full h-full" preserveAspectRatio="none">
            <rect x="10" y="28" width="24" height="2" fill="#0B0B0C" />
            <line x1="14" y1="30" x2="30" y2="38" stroke="#0B0B0C" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="30" y1="30" x2="14" y2="38" stroke="#0B0B0C" strokeWidth="1.2" strokeLinecap="round" />
            <motion.path
              d="M 36 28 C 260 24, 360 16, 600 12 S 940 6, 1150 4"
              stroke="#B98B24"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1.6, ease: [0.16, 0.84, 0.24, 1] }}
            />
            <circle cx="1160" cy="3" r="2.5" fill="#B98B24" />
            <circle cx="1170" cy="8" r="2.5" fill="#B98B24" />
            <circle cx="1178" cy="2" r="2.5" fill="#B98B24" />
            <circle cx="1184" cy="10" r="2.5" fill="#B98B24" />
            <circle cx="1190" cy="5" r="2.5" fill="#B98B24" />
          </svg>
        </div>

        {/* Three editorial areas — tightened, aligned with trajectory above */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {PILLARS.map((p, i) => (
            <motion.article
              key={p.kicker}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="relative"
              data-testid={`pillar-${i + 1}`}
            >
              {/* Alignment dot — anchors each pillar to the trajectory ribbon above */}
              <span aria-hidden className="hidden lg:block absolute -top-7 left-0 w-2 h-2 rounded-full bg-[#B98B24]" />
              <span aria-hidden className="lg:hidden block w-12 h-px bg-[#B98B24] mb-4" />
              <p className="oversize text-[12px] tracking-[0.3em] uppercase text-[#B98B24] mb-3">
                0{i + 1} · {p.kicker}
              </p>
              <h3 className="font-display text-2xl md:text-[26px] lg:text-[30px] leading-[1.08] tracking-[-0.01em] text-[#0B0B0C] max-w-sm">
                {p.headline}
              </h3>
              <p className="mt-3 text-[#343437] leading-relaxed text-[15px] max-w-md">
                {p.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
